import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ActivityIndicator, ScrollView, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { Row, SortingState } from '@tanstack/react-table'
import { uniqueId } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { COMMON_MIXPANEL_EVENTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  getDocumentUploadErrorMessage,
  isFileNameAlreadyExistsError,
} from '@lyrahealth-inc/shared-app-logic/src/features/documents/documentErrorUtils'
import {
  DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH,
  DOCUMENT_UPLOAD_MAX_FILE_SIZE,
} from '@lyrahealth-inc/shared-app-logic/src/features/documents/documentValidation'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  DocumentType as APIDocumentType,
  DocumentUserType,
  EnvelopeStatus,
  RecipientStatus,
} from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'
import { EpisodeParticipantRole } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { CONVERSATION_TYPE, MESSAGE_AUTHOR_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { FileUpload } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/FileUpload'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { RadioButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { DownloadFileIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/DownloadFileIcon'
import { EditIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/EditIcon'
import { ExternalLinkIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExternalLinkIcon'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { ShareIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ShareIcon'
import { SignatureIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SignatureIcon'
import { TrashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TrashIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { Image } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/image/Image'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { UnstyledIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/unstyledIconButton/UnstyledIconButton'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import { DocumentFilters } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFilters'
import {
  DocumentFiltersState,
  DocumentStatus,
  DocumentType,
  FilterSectionConfig,
  INITIAL_DOCUMENT_FILTER_STATE,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFiltersPopover'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { HorizontalTabsContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/horizontalTabsContainer/HorizontalTabsContainer'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { MessageAttachmentType } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Messages'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  useCreateDocumentFromTemplateMutation,
  useDeleteDocumentMutation,
  useEditDocumentMutation,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
  useLazyGetDraftSenderViewUrlQuery,
  useLazyGetSigningUrlQuery,
  useShareDocumentMutation,
  useUploadDocumentMutation,
} from './data/documentsApi'
import { DocuSignEmbeddedView, DocuSignEvent } from './DocuSignEmbeddedView'
import useClientDocumentsColumns from './hooks/useClientDocumentsColumns'
import { actions, mixpanelEvents, mixpanelPages } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { DA_ROLES } from '../../common/constants/appConstants'
import {
  getAuthIsLTSupervisor,
  getAuthRoles,
  getAuthUserFirstName,
  getAuthUserFullName,
  getAuthUserId,
} from '../../data/auth/authSelectors'
import { baseApi, RTKQueryTag } from '../../data/baseApi'
import { useGuardianForSelectedClient } from '../../hooks/useGuardianForSelectedClient'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'
import { useGetDocumentTemplatesQuery } from '../documents/data/templatesApi'
import { useGetConcurrentCareQuery } from '../episodes/data/concurrentCareApi'
import { useCurrentEpisodeForClient } from '../episodes/data/hooks/useCurrentEpisodeForClient'
import {
  useCreateConversationMutation,
  useGetPatientConversationsQuery,
  useSendMessageMutation,
} from '../messages/data/messagesApi'

// Constant mappings between UI and API document types
const UI_TO_API_DOCUMENT_TYPE: Record<DocumentType, APIDocumentType> = {
  [DocumentType.INTAKE_FORM]: APIDocumentType.INTAKE_FORM,
  [DocumentType.ASSESSMENT]: APIDocumentType.ASSESSMENT,
  [DocumentType.ACTIVITY]: APIDocumentType.ACTIVITY,
  [DocumentType.OTHER]: APIDocumentType.OTHER,
}

const API_TO_UI_DOCUMENT_TYPE: Record<APIDocumentType, DocumentType> = {
  [APIDocumentType.INTAKE_FORM]: DocumentType.INTAKE_FORM,
  [APIDocumentType.ASSESSMENT]: DocumentType.ASSESSMENT,
  [APIDocumentType.ACTIVITY]: DocumentType.ACTIVITY,
  [APIDocumentType.OTHER]: DocumentType.OTHER,
}

// Utility functions for file type checking
const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/')
}

const isPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf'
}

export type DocumentItem = {
  id: string
  fileName: string
  status: DocumentStatus
  type: DocumentType
  updatedOn: string
  addedBy: string
  uploadedForProvider?: string
  uploadedForClient?: string
  createDate: string
  viewedDate?: string | null
  createdByType: DocumentUserType
  creatorId: string
  isOwnedByConcurrentProvider: boolean
  envelopeStatus?: EnvelopeStatus
  signedCount?: number
  totalSigners?: number
  clientSignedDate?: string | null
  providerSignedDate?: string | null
}

const Container = styled.View(({ theme }) => ({
  padding: theme.spacing['24px'],
  paddingTop: 0,
  backgroundColor: theme.colors.backgroundPrimary,
  gap: theme.spacing['16px'],
  width: '100%',
}))

const FilterRowWrapper = styled.View({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 10,
})

const FilterRow = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const SearchIconWrapper = styled.View({
  width: 24,
  height: 24,
  justifyContent: 'center',
  alignItems: 'center',
})

const RowActionsContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['12px'],
  padding: theme.spacing['8px'],
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: 58,
  backgroundColor: theme.colors.backgroundPrimary,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
}))

const ActionIconContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['4px'],
}))

const EditModalContent = styled.View(({ theme }) => ({
  padding: theme.spacing['24px'],
  gap: theme.spacing['16px'],
}))

const ShareModalContent = styled.View(({ theme }) => ({
  gap: theme.spacing['24px'],
}))

const EditModalHeader = styled.View(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const EditModalButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: theme.spacing['8px'],
  marginTop: theme.spacing['8px'],
}))

const EmptyTableText = styled(BodyText)(({ theme }) => ({
  margin: theme.spacing['48px'],
  textAlign: 'center',
}))

const UploadModalContainer = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.backgroundPrimary,
  display: 'flex',
  flexDirection: 'column',
}))

const UploadModalHeader = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing['24px'],
}))

const UploadModalHeaderButtons = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const UploadModalContent = styled.View({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const UploadTabContent = styled.View(({ theme }) => ({
  flex: 1,
  padding: theme.spacing['24px'],
}))

const UploadTabSplitContainer = styled.View({
  flex: 1,
  flexDirection: 'row',
})

const UploadTabLeftPane = styled.View(({ theme }) => ({
  flex: 1,
  paddingRight: theme.spacing['24px'],
}))

const UploadTabRightPane = styled.View({
  flex: 1,
})

const FilePreviewContainer = styled.View(({ theme }) => ({
  flex: 1,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  backgroundColor: theme.colors.backgroundSecondary,
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}))

const FilePreviewEmptyState = styled.View(({ theme }) => ({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing['24px'],
}))

const LibraryContainer = styled.View(({ theme }) => ({
  flex: 1,
  alignItems: 'center',
  paddingTop: theme.spacing['8px'],
}))

const LibraryInner = styled.View({
  width: '100%',
  maxWidth: 600,
  flex: 1,
  flexDirection: 'column',
})

const LibraryList = styled(ScrollView)(({ theme }) => ({
  maxHeight: 320,
  marginTop: theme.spacing['16px'],
}))

const LibraryContentBorder = styled.View(({ theme }) => ({
  borderWidth: 1,
  borderColor: theme.colors.borderDefault,
  borderRadius: 16,
  marginTop: theme.spacing['16px'],
  marginBottom: theme.spacing['16px'],
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
}))

const LibraryItem = styled.View<{ isLast?: boolean }>(({ theme, isLast }) => ({
  paddingVertical: theme.spacing['16px'],
  ...(!isLast && {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderDefault,
  }),
}))

const LibraryEmptyState = styled.View(({ theme }) => ({
  paddingVertical: theme.spacing['24px'],
  alignItems: 'center',
}))

const LibraryShareSection = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['16px'],
}))

const LibraryItemTitleRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const LibraryItemSignatureIconWrapper = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['4px'],
  alignSelf: 'center',
}))

export const ClientDocuments: React.FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { colors, spacing } = useTheme()
  const { data: clientDetails } = useSelectedClientData()
  const authRoles = useSelector(getAuthRoles)
  const isDaProvider = DA_ROLES.some((role) => authRoles.includes(role))
  const providerId = useSelector(getAuthUserId)
  const isLTSupervisor = useSelector(getAuthIsLTSupervisor)
  const supervisorProviderId = isLTSupervisor ? providerId : undefined
  const providerFullName = useSelector(getAuthUserFullName)
  const providerFirstName = useSelector(getAuthUserFirstName)
  const patientId = clientDetails?.id ?? ''
  const { clientDetailsSelectedEpisodeId }: { clientDetailsSelectedEpisodeId?: string } = useOutletContext() ?? {}
  const { data: documentsData, isLoading: isLoadingDocuments } = useGetDocumentsQuery(
    { providerId, patientId },
    { skip: !providerId || !patientId },
  )
  const { data: concurrentCare = [] } = useGetConcurrentCareQuery({ patientId }, { skip: !patientId })
  const otherConcurrentProviders = useMemo(
    () => concurrentCare.filter(({ provider }) => provider.id !== providerId),
    [concurrentCare, providerId],
  )
  const concurrentProviderNameById = useMemo(
    () => Object.fromEntries(otherConcurrentProviders.map(({ provider }) => [provider.id, provider.name])),
    [otherConcurrentProviders],
  )
  const concurrentEpisodeIds = useMemo(
    () => new Set(otherConcurrentProviders.map(({ episodeId }) => episodeId)),
    [otherConcurrentProviders],
  )
  const providerNameByEpisodeId = useMemo(
    () => Object.fromEntries(otherConcurrentProviders.map(({ episodeId, provider }) => [episodeId, provider.name])),
    [otherConcurrentProviders],
  )
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation()
  const [triggerDownload] = useLazyDownloadDocumentQuery()
  const [getDraftSenderViewUrl] = useLazyGetDraftSenderViewUrlQuery()
  const [getSigningUrl] = useLazyGetSigningUrlQuery()
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation()
  const [shareDocument, { isLoading: isSharing }] = useShareDocumentMutation()
  const [editDocument, { isLoading: isEditing }] = useEditDocumentMutation()
  const [createDocumentFromTemplate, { isLoading: isCreatingFromTemplate }] = useCreateDocumentFromTemplateMutation()
  const { isDocumentMessageEnabled } = useFlags()
  const { data: templatesData, isLoading: isLoadingTemplates } = useGetDocumentTemplatesQuery(
    supervisorProviderId ? { providerId: supervisorProviderId } : undefined,
  )
  const [createMessage] = useSendMessageMutation()
  const [createConversation] = useCreateConversationMutation()
  const { currentData: patientConversations } = useGetPatientConversationsQuery(
    providerId && patientId ? { providerId, patientId, types: CONVERSATION_TYPE.PROVIDER } : skipToken,
  )
  const { guardian } = useGuardianForSelectedClient(clientDetailsSelectedEpisodeId)
  const patientConversationId = patientConversations?.find(
    (conversation) => conversation.patient_lyra_id === patientId,
  )?.conversation_id
  const caregiverConversationId = patientConversations?.find(
    (conversation) => conversation.patient_lyra_id === guardian.guardianLyraId,
  )?.conversation_id
  const { currentEpisode } = useCurrentEpisodeForClient(clientDetailsSelectedEpisodeId)
  const isDBTTeens = currentEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens

  const [searchTerm, setSearchTerm] = useState('')
  const [filterState, setFilterState] = useState<DocumentFiltersState>(INITIAL_DOCUMENT_FILTER_STATE)
  const [sortingState, setSortingState] = useState<SortingState>([{ id: 'updatedOn', desc: true }])
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)

  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(null)
  const [documentToShare, setDocumentToShare] = useState<DocumentItem | null>(null)
  const [documentToEdit, setDocumentToEdit] = useState<DocumentItem | null>(null)
  const [editFileName, setEditFileName] = useState('')
  const [editDocumentType, setEditDocumentType] = useState<DocumentType>(DocumentType.OTHER)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadDocumentType, setUploadDocumentType] = useState<DocumentType | ''>(
    isDaProvider ? DocumentType.INTAKE_FORM : DocumentType.ASSESSMENT,
  )
  const [uploadFileName, setUploadFileName] = useState('')
  const [uploadSignatureRequired, setUploadSignatureRequired] = useState('')
  const [uploadShareDocument, setUploadShareDocument] = useState(false)
  const [uploadShareMessage, setUploadShareMessage] = useState('')
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null)
  const [activeUploadTab, setActiveUploadTab] = useState('fromComputer')
  const [librarySearchTerm, setLibrarySearchTerm] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [libraryShareDocument, setLibraryShareDocument] = useState(false)
  const [libraryShareMessage, setLibraryShareMessage] = useState('')
  const [libraryConfirmVisible, setLibraryConfirmVisible] = useState(false)
  const [libraryErrorMessage, setLibraryErrorMessage] = useState<string | null>(null)
  const [uploadShareWithCaregiver, setUploadShareWithCaregiver] = useState(false)

  // DocuSign state
  const [isDocuSignModalOpen, setIsDocuSignModalOpen] = useState(false)
  const [docuSignUrl, setDocuSignUrl] = useState<string | null>(null)
  const [docuSignDocumentId, setDocuSignDocumentId] = useState<string | null>(null)
  const [docuSignDocumentName, setDocuSignDocumentName] = useState<string>('')
  const [docuSignMode, setDocuSignMode] = useState<'upload' | 'draft' | 'sign' | null>(null)
  const latestDraftRequestIdRef = useRef<string | null>(null)

  const clientName = clientDetails?.full_name || clientDetails?.first_name || 'the client'
  const teenFirstName = clientDetails?.preferred_first_name ?? clientDetails?.first_name ?? clientName
  const caregiverDisplayName = guardian.guardianName || 'Caregiver'
  const guardianLyraId = guardian.guardianLyraId

  const documentTypeOptions = [
    ...(isDaProvider ? [{ label: DocumentType.INTAKE_FORM, value: DocumentType.INTAKE_FORM }] : []),
    { label: DocumentType.ASSESSMENT, value: DocumentType.ASSESSMENT },
    { label: DocumentType.ACTIVITY, value: DocumentType.ACTIVITY },
    { label: DocumentType.OTHER, value: DocumentType.OTHER },
  ]

  const signatureRequiredOptions = [
    {
      label: intl.formatMessage({ defaultMessage: 'No signature required', description: 'Signature option' }),
      value: 'none',
    },
    {
      label: intl.formatMessage({ defaultMessage: 'From client only', description: 'Signature option' }),
      value: 'client',
    },
    {
      label: intl.formatMessage({ defaultMessage: 'From client and provider', description: 'Signature option' }),
      value: 'both',
    },
  ]

  useEffect(() => {
    if (!patientId) return
    track({
      event: COMMON_MIXPANEL_EVENTS.PAGE_LOAD,
      page: mixpanelPages.CLIENT_DOCUMENTS,
      properties: { client_id: patientId },
    })
  }, [patientId])

  // Create/cleanup preview URL when file changes
  useEffect(() => {
    if (!uploadFile) {
      setFilePreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(uploadFile)
    setFilePreviewUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [uploadFile])

  const handleDownload = useCallback(
    async (doc: DocumentItem) => {
      try {
        const result = await triggerDownload({ documentId: doc.id, providerId: supervisorProviderId }).unwrap()
        const url = URL.createObjectURL(result)
        const link = document.createElement('a')
        link.href = url
        link.download = doc.fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Failed to download document:', error)
      }
    },
    [triggerDownload, supervisorProviderId],
  )

  const handleViewDocument = useCallback(
    async (doc: DocumentItem) => {
      try {
        const result = await triggerDownload({ documentId: doc.id, providerId: supervisorProviderId }).unwrap()
        if (result.type.startsWith('image/') || result.type === 'application/pdf') {
          const url = URL.createObjectURL(result)
          window.open(url, '_blank', 'noopener,noreferrer')
          setTimeout(() => URL.revokeObjectURL(url), 10000)
        } else {
          await handleDownload(doc)
        }
      } catch (error) {
        console.error('Failed to view document:', error)
      }
    },
    [handleDownload, supervisorProviderId, triggerDownload],
  )

  const handleRowClick = useCallback(
    (row: Row<DocumentItem>) => {
      handleViewDocument(row.original)
    },
    [handleViewDocument],
  )

  const handleEditClick = useCallback(
    (doc: DocumentItem) => {
      setDocumentToEdit(doc)
      setEditFileName(doc.fileName)
      setEditDocumentType(!isDaProvider && doc.type === DocumentType.INTAKE_FORM ? DocumentType.ASSESSMENT : doc.type)
    },
    [isDaProvider],
  )

  const handleEditConfirm = useCallback(async () => {
    if (!documentToEdit) return
    try {
      await editDocument({
        documentId: documentToEdit.id,
        fileName: editFileName,
        documentType: UI_TO_API_DOCUMENT_TYPE[editDocumentType],
        providerId: supervisorProviderId,
      }).unwrap()
      setDocumentToEdit(null)
      dispatch(
        setToastContent({
          text: intl.formatMessage(
            {
              defaultMessage: '{fileName} updated',
              description: 'Success message when document is updated',
            },
            { fileName: editFileName },
          ),
          id: uniqueId('edit-document-success-'),
          toastType: 'success',
        }),
      )
    } catch (error) {
      console.error('Failed to edit document:', error)
      setDocumentToEdit(null)
      dispatch(
        setToastContent({
          text: intl.formatMessage({
            defaultMessage: 'Failed to update document. Please try again.',
            description: 'Error message when document update fails',
          }),
          id: uniqueId('edit-document-error-'),
          toastType: 'error',
        }),
      )
    }
  }, [editDocument, documentToEdit, editFileName, editDocumentType, dispatch, intl, supervisorProviderId])

  const handleEditCancel = () => {
    setDocumentToEdit(null)
  }

  const handleShareClick = useCallback((doc: DocumentItem) => {
    setDocumentToShare(doc)
  }, [])

  const sendDocumentMessage = useCallback(
    ({
      messageBody,
      documentId,
      fileName,
      targetConversationId,
    }: {
      messageBody: string
      documentId: string
      fileName: string
      targetConversationId: string | undefined
    }) => {
      if (!isDocumentMessageEnabled || !targetConversationId) return
      createMessage({
        conversationId: targetConversationId,
        data: {
          message_body: messageBody,
          message_type: 'direct',
          message_author_id: providerId ?? '',
          message_author_type: MESSAGE_AUTHOR_TYPE.PROVIDER,
          metadata: {
            attachments: [
              {
                key: uniqueId('doc-attachment-'),
                type: MessageAttachmentType.DOCUMENT,
                documentId,
                fileName,
              },
            ],
          },
        },
      })
    },
    [isDocumentMessageEnabled, createMessage, providerId],
  )

  const getOrCreateDocumentConversationId = useCallback(
    async (conversationId: string | undefined, recipientId: string | undefined) => {
      if (
        !isDocumentMessageEnabled ||
        conversationId ||
        !clientDetailsSelectedEpisodeId ||
        !providerId ||
        !recipientId
      ) {
        return conversationId
      }
      try {
        const conversation = await createConversation({
          episodeId: clientDetailsSelectedEpisodeId,
          participants: [
            { participant_lyra_id: providerId, type: 'provider' },
            { participant_lyra_id: recipientId, type: 'patient' },
          ],
        }).unwrap()
        return conversation.external_conversation_id
      } catch (error) {
        console.error('Failed to create document message conversation:', error)
        return undefined
      }
    },
    [clientDetailsSelectedEpisodeId, createConversation, isDocumentMessageEnabled, providerId],
  )

  const handleShareConfirm = useCallback(async () => {
    if (!documentToShare || !patientId) return
    try {
      await shareDocument({
        documentId: documentToShare.id,
        recipientId: patientId,
        providerId: supervisorProviderId,
      }).unwrap()
      const targetConversationId = await getOrCreateDocumentConversationId(patientConversationId, patientId)
      sendDocumentMessage({
        messageBody: uploadShareMessage || documentToShare.fileName,
        documentId: documentToShare.id,
        fileName: documentToShare.fileName,
        targetConversationId,
      })
      dispatch(
        setToastContent({
          text: intl.formatMessage(
            {
              defaultMessage: '{fileName} shared',
              description: 'Success message when document is shared with client',
            },
            { fileName: documentToShare.fileName },
          ),
          id: uniqueId('share-document-success-'),
          toastType: 'success',
        }),
      )
      setDocumentToShare(null)
      setUploadShareMessage('')
    } catch (error) {
      console.error('Failed to share document:', error)
      setDocumentToShare(null)
      dispatch(
        setToastContent({
          text: intl.formatMessage({
            defaultMessage: 'Failed to share document. Please try again.',
            description: 'Error message when document sharing fails',
          }),
          id: uniqueId('share-document-error-'),
          toastType: 'error',
        }),
      )
    }
  }, [
    shareDocument,
    documentToShare,
    patientId,
    dispatch,
    intl,
    supervisorProviderId,
    sendDocumentMessage,
    getOrCreateDocumentConversationId,
    uploadShareMessage,
    patientConversationId,
  ])

  const handleShareCancel = () => {
    setUploadShareMessage('')
    setDocumentToShare(null)
  }

  const handleDeleteClick = useCallback((doc: DocumentItem) => {
    setDocumentToDelete(doc)
  }, [])

  const handleDeleteConfirm = async () => {
    if (documentToDelete) {
      try {
        await deleteDocument({ documentId: documentToDelete.id, providerId: supervisorProviderId }).unwrap()
        setDocumentToDelete(null)
      } catch (error) {
        console.error('Failed to delete document:', error)
        setDocumentToDelete(null)
      }
    }
  }

  const handleDeleteCancel = () => {
    setDocumentToDelete(null)
  }

  const handleUpload = () => {
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.UPLOAD_DOCUMENT,
      page: mixpanelPages.CLIENT_DOCUMENTS,
      properties: { client_id: patientId },
    })
    setIsUploadModalOpen(true)
  }

  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false)
    setUploadFile(null)
    setUploadFileName('')
    setUploadDocumentType(isDaProvider ? DocumentType.INTAKE_FORM : DocumentType.ASSESSMENT)
    setUploadSignatureRequired('')
    setUploadShareDocument(false)
    setUploadShareMessage('')
    setUploadErrorMessage(null)
    setActiveUploadTab('fromComputer')
    setLibrarySearchTerm('')
    setSelectedTemplateId(null)
    setLibraryShareDocument(false)
    setLibraryShareMessage('')
    setLibraryConfirmVisible(false)
    setLibraryErrorMessage(null)
    setUploadShareWithCaregiver(false)
  }, [isDaProvider])

  const filteredLibraryDocuments = useMemo(() => {
    const templates = templatesData?.document_templates ?? []
    if (!librarySearchTerm.trim()) return templates
    return templates.filter((t) => t.file_name.toLowerCase().includes(librarySearchTerm.toLowerCase()))
  }, [templatesData, librarySearchTerm])

  const selectedTemplate = useMemo(
    () => (templatesData?.document_templates ?? []).find((t) => t.document_template_id === selectedTemplateId) ?? null,
    [templatesData, selectedTemplateId],
  )

  const handleFromLibraryInitiate = () => {
    if (!selectedTemplateId) return
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.UPLOAD_DOCUMENT_FROM_LIBRARY,
      page: mixpanelPages.CLIENT_DOCUMENTS,
      properties: { client_id: patientId },
    })
    setLibraryConfirmVisible(true)
  }

  const handleFromLibraryConfirm = async () => {
    if (!clientDetailsSelectedEpisodeId || !selectedTemplateId) return
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.CONFIRM_UPLOAD_DOCUMENT_FROM_LIBRARY,
      page: mixpanelPages.CLIENT_DOCUMENTS,
      properties: { client_id: patientId },
    })
    const isShared = !!selectedTemplate?.signing_flow || libraryShareDocument
    try {
      const result = await createDocumentFromTemplate({
        documentTemplateId: selectedTemplateId,
        episodeId: clientDetailsSelectedEpisodeId,
        isShared,
        providerId: supervisorProviderId,
      }).unwrap()
      setLibraryConfirmVisible(false)
      if (isShared) {
        const targetConversationId = await getOrCreateDocumentConversationId(patientConversationId, patientId)
        sendDocumentMessage({
          messageBody: selectedTemplate?.signing_flow
            ? intl.formatMessage(
                {
                  defaultMessage: '{providerFirstName} has sent you a document to review',
                  description: 'Auto-generated message sent when a DocuSign document is shared with the client',
                },
                { providerFirstName: providerFirstName ?? providerFullName },
              )
            : libraryShareMessage ||
              selectedTemplate?.file_name ||
              intl.formatMessage({
                defaultMessage: 'Document shared',
                description:
                  'Fallback message body when a library document has no file name and the provider entered no custom message',
              }),
          documentId: result.document_id,
          fileName: selectedTemplate?.file_name ?? selectedTemplateId,
          targetConversationId,
        })
      }
      handleUploadModalClose()
      dispatch(
        setToastContent({
          toastType: 'success',
          text: intl.formatMessage(
            isShared
              ? {
                  defaultMessage: '{documentName} uploaded and shared',
                  description: 'Success message when document is created from template and shared with client',
                }
              : {
                  defaultMessage: '{documentName} uploaded',
                  description: 'Success message when document is created from template',
                },
            { documentName: selectedTemplate?.file_name ?? selectedTemplateId },
          ),
          id: uniqueId('from-template-success-'),
        }),
      )
    } catch (error) {
      setLibraryConfirmVisible(false)
      setLibraryErrorMessage(getDocumentUploadErrorMessage(error, intl))
    }
  }

  const resetDocuSignState = useCallback(() => {
    setDocuSignUrl(null)
    setIsDocuSignModalOpen(false)
    setDocuSignDocumentId(null)
    setDocuSignDocumentName('')
    setDocuSignMode(null)
  }, [])

  const signingFlowMap: Record<string, string> = {
    client: 'PATIENT_ONLY',
    both: 'PATIENT_THEN_PROVIDER',
  }

  const handleUploadConfirm = async () => {
    if (!uploadFile || !uploadFileName || !clientDetailsSelectedEpisodeId || !uploadDocumentType) return
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.UPLOAD_DOCUMENT_FROM_COMPUTER,
      page: mixpanelPages.CLIENT_DOCUMENTS,
      properties: { client_id: patientId },
    })

    if (uploadSignatureRequired && uploadSignatureRequired !== 'none') {
      try {
        const returnUrl = `${window.location.origin}/docusign-complete.html`
        const result = await uploadDocument({
          file: uploadFile,
          fileName: uploadFileName,
          documentType: UI_TO_API_DOCUMENT_TYPE[uploadDocumentType],
          episodeId: clientDetailsSelectedEpisodeId,
          isShared: uploadShareDocument,
          signatureRequired: true,
          signingFlow: signingFlowMap[uploadSignatureRequired] ?? 'PATIENT_ONLY',
          returnUrl,
          providerId: supervisorProviderId,
        }).unwrap()

        if (result.sender_view_url) {
          setDocuSignUrl(result.sender_view_url)
          setDocuSignDocumentId(result.document_id)
          setDocuSignDocumentName(uploadFileName)
          setIsUploadModalOpen(false)
          setIsDocuSignModalOpen(true)
        } else {
          handleUploadModalClose()
        }
      } catch (error) {
        console.error('[DocuSign] Failed to upload document with signature:', error)
        if (isFileNameAlreadyExistsError(error)) {
          setUploadErrorMessage(getDocumentUploadErrorMessage(error, intl))
        } else {
          dispatch(
            setToastContent({
              toastType: 'error',
              text: intl.formatMessage({
                defaultMessage: 'Failed to initiate DocuSign. Please try again.',
                description: 'Error message when DocuSign envelope creation fails',
              }),
              id: uniqueId('docusign-error-'),
            }),
          )
        }
      }
    } else {
      // No signature required, proceed with regular upload
      try {
        const result = await uploadDocument({
          file: uploadFile,
          fileName: uploadFileName,
          documentType: UI_TO_API_DOCUMENT_TYPE[uploadDocumentType],
          episodeId: clientDetailsSelectedEpisodeId,
          isShared: isDBTTeens ? false : uploadShareDocument,
          providerId: supervisorProviderId,
        }).unwrap()
        if (isDBTTeens && uploadShareDocument && patientId) {
          await shareDocument({
            documentId: result.document_id,
            recipientId: patientId,
            providerId: supervisorProviderId,
          }).unwrap()
        }
        if (uploadShareWithCaregiver && guardianLyraId) {
          await shareDocument({
            documentId: result.document_id,
            recipientId: guardianLyraId,
            providerId: supervisorProviderId,
          }).unwrap()
        }
        if (uploadShareDocument) {
          const targetConversationId = await getOrCreateDocumentConversationId(patientConversationId, patientId)
          sendDocumentMessage({
            messageBody: uploadShareMessage || uploadFileName,
            documentId: result.document_id,
            fileName: uploadFileName,
            targetConversationId,
          })
        }
        if (uploadShareWithCaregiver) {
          const targetConversationId = await getOrCreateDocumentConversationId(caregiverConversationId, guardianLyraId)
          sendDocumentMessage({
            messageBody: uploadShareMessage || uploadFileName,
            documentId: result.document_id,
            fileName: uploadFileName,
            targetConversationId,
          })
        }
        handleUploadModalClose()
        dispatch(
          setToastContent({
            toastType: 'success',
            text: intl.formatMessage(
              uploadShareDocument || uploadShareWithCaregiver
                ? {
                    defaultMessage: '{documentName} uploaded and shared',
                    description: 'Success message when document is uploaded and shared with a client or caregiver',
                  }
                : {
                    defaultMessage: '{documentName} uploaded',
                    description: 'Success message when document is uploaded',
                  },
              { documentName: uploadFileName },
            ),
            id: uniqueId('upload-success-'),
          }),
        )
      } catch (error) {
        console.error('Failed to upload document:', error)
        setUploadErrorMessage(getDocumentUploadErrorMessage(error, intl))
      }
    }
  }

  const handleDocuSignComplete = async (event: DocuSignEvent) => {
    const isSuccess = event === 'send' || event === 'signing_complete'
    const isDraft = event === 'cancel' || event === 'save'
    const documentId = docuSignDocumentId
    const documentName = docuSignDocumentName
    handleUploadModalClose()
    if (docuSignMode === 'sign') {
      if (isSuccess) {
        dispatch(baseApi.util.invalidateTags([RTKQueryTag.DOCUMENT]))
        dispatch(
          setToastContent({
            toastType: 'success',
            text: intl.formatMessage(
              {
                defaultMessage: '{fileName} signed',
                description: 'Success message when provider signs a document via DocuSign',
              },
              { fileName: documentName },
            ),
            id: uniqueId('docusign-provider-signed-'),
          }),
        )
      }
      resetDocuSignState()
      return
    }
    if (isSuccess) {
      if (documentId && patientId) {
        try {
          await shareDocument({ documentId, recipientId: patientId, providerId: supervisorProviderId }).unwrap()
          const targetConversationId = await getOrCreateDocumentConversationId(patientConversationId, patientId)
          sendDocumentMessage({
            messageBody: intl.formatMessage(
              {
                defaultMessage: '{providerFirstName} has sent you a document to review',
                description: 'Auto-generated message sent when a DocuSign document is shared with the client',
              },
              { providerFirstName: providerFirstName ?? providerFullName },
            ),
            documentId,
            fileName: documentName,
            targetConversationId,
          })
          dispatch(
            setToastContent({
              toastType: 'success',
              text:
                docuSignMode === 'draft'
                  ? intl.formatMessage(
                      {
                        defaultMessage: '{fileName} sent and shared',
                        description: 'Success message when a draft document is sent and shared via DocuSign',
                      },
                      { fileName: documentName },
                    )
                  : intl.formatMessage(
                      {
                        defaultMessage: '{fileName} uploaded and shared',
                        description: 'Success message when document is uploaded and shared via DocuSign',
                      },
                      { fileName: documentName },
                    ),
              id: uniqueId('docusign-upload-share-success-'),
            }),
          )
        } catch (error) {
          console.error('[DocuSign] Failed to share document:', error)
          dispatch(
            setToastContent({
              toastType: 'error',
              text: intl.formatMessage({
                defaultMessage: 'Failed to share document. Please try again.',
                description: 'Error message when document sharing fails after DocuSign',
              }),
              id: uniqueId('docusign-share-error-'),
            }),
          )
        }
      }
    } else if (isDraft) {
      handleDocuSignSavedAsDraft(documentName)
    } else {
      if (documentId) {
        try {
          await deleteDocument({ documentId, providerId: supervisorProviderId }).unwrap()
        } catch (deleteError) {
          console.error('[DocuSign] Failed to delete orphaned document:', deleteError)
        }
      }
      dispatch(
        setToastContent({
          toastType: 'error',
          text: intl.formatMessage({
            defaultMessage: 'The DocuSign process was not completed. Please try again.',
            description: 'Error message when DocuSign process ends without a successful send or signing',
          }),
          id: uniqueId('docusign-incomplete-'),
        }),
      )
    }
    resetDocuSignState()
  }

  const handleDocuSignSavedAsDraft = (documentName: string) => {
    dispatch(baseApi.util.invalidateTags([RTKQueryTag.DOCUMENT]))
    dispatch(
      setToastContent({
        toastType: 'success',
        text: intl.formatMessage(
          {
            defaultMessage: '{fileName} saved as draft',
            description: 'Message when DocuSign envelope is saved as a draft',
          },
          { fileName: documentName },
        ),
        id: uniqueId('docusign-draft-'),
      }),
    )
  }

  const handleDocuSignCancel = () => {
    handleUploadModalClose()
    if (docuSignMode !== 'sign') {
      handleDocuSignSavedAsDraft(docuSignDocumentName)
    }
    resetDocuSignState()
  }

  const handleDocuSignError = async (error: string) => {
    console.error('[DocuSign] Error:', error)
    handleUploadModalClose()
    if (docuSignMode === 'upload' && docuSignDocumentId) {
      try {
        await deleteDocument({ documentId: docuSignDocumentId, providerId: supervisorProviderId }).unwrap()
      } catch (deleteError) {
        console.error('[DocuSign] Failed to delete orphaned document:', deleteError)
      }
    }
    dispatch(
      setToastContent({
        toastType: 'error',
        text: intl.formatMessage({
          defaultMessage: 'Failed to load DocuSign. Please try again.',
          description: 'Error message when DocuSign iframe fails to load',
        }),
        id: uniqueId('docusign-error-'),
      }),
    )
    resetDocuSignState()
  }

  const handleOpenDraftInDocuSign = useCallback(
    async (doc: DocumentItem) => {
      const returnUrl = `${window.location.origin}/docusign-complete.html`
      const requestedDocumentId = doc.id
      latestDraftRequestIdRef.current = requestedDocumentId
      setDocuSignUrl(null)
      setDocuSignDocumentId(requestedDocumentId)
      setDocuSignDocumentName(doc.fileName)
      setDocuSignMode('draft')
      setIsDocuSignModalOpen(true)
      try {
        const result = await getDraftSenderViewUrl({
          documentId: requestedDocumentId,
          returnUrl,
          providerId: supervisorProviderId,
        }).unwrap()
        if (latestDraftRequestIdRef.current !== requestedDocumentId) {
          return
        }
        setDocuSignUrl(result.sender_view_url)
      } catch {
        resetDocuSignState()
        dispatch(
          setToastContent({
            toastType: 'error',
            text: intl.formatMessage({
              defaultMessage: 'Failed to open draft in DocuSign. Please try again.',
              description: 'Error message when fetching draft sender view URL fails',
            }),
            id: uniqueId('docusign-draft-open-error-'),
          }),
        )
      }
    },
    [getDraftSenderViewUrl, dispatch, intl, resetDocuSignState, supervisorProviderId],
  )

  const handleSignInDocuSign = useCallback(
    async (doc: DocumentItem) => {
      const returnUrl = `${window.location.origin}/docusign-complete.html`
      setDocuSignUrl(null)
      setDocuSignDocumentId(doc.id)
      setDocuSignDocumentName(doc.fileName)
      setDocuSignMode('sign')
      setIsDocuSignModalOpen(true)
      try {
        const result = await getSigningUrl({
          documentId: doc.id,
          returnUrl,
          providerId: supervisorProviderId,
        }).unwrap()
        setDocuSignUrl(result.signing_url)
      } catch {
        resetDocuSignState()
        dispatch(
          setToastContent({
            toastType: 'error',
            text: intl.formatMessage({
              defaultMessage: 'Failed to open document in DocuSign. Please try again.',
              description: 'Error message when fetching DocuSign signing URL fails',
            }),
            id: uniqueId('docusign-signing-open-error-'),
          }),
        )
      }
    },
    [getSigningUrl, dispatch, intl, resetDocuSignState, supervisorProviderId],
  )

  const unknownProviderName = intl.formatMessage({
    defaultMessage: 'A Lyra Provider',
    description: 'Fallback display name for a provider whose name cannot be resolved',
  })
  const teenLabel = isDBTTeens
    ? intl.formatMessage(
        { defaultMessage: '{name} (teen)', description: 'Added By label for teen in DBT Teens program' },
        { name: teenFirstName },
      )
    : ''
  const caregiverLabel = isDBTTeens
    ? intl.formatMessage(
        { defaultMessage: '{name} (caregiver)', description: 'Added By label for caregiver in DBT Teens program' },
        { name: caregiverDisplayName },
      )
    : ''

  // Transform API documents to UI DocumentItem format
  const documents: DocumentItem[] = useMemo(() => {
    if (!documentsData?.documents) return []
    return documentsData.documents.map((doc) => {
      const recipients = doc.envelope?.recipients
      const patientShares = doc.shares?.filter((share) => share.recipient_type === DocumentUserType.PATIENT) ?? []
      const isSharedWithPatientRecipient = Boolean(doc.is_shared_with_patient || patientShares.length)
      const hasUnviewedPatientShare = patientShares.length
        ? patientShares.some((share) => !share.first_viewed_date)
        : !doc.patient_first_viewed_date
      const allRecipientsCompleted =
        !!recipients?.length && recipients.every((r) => r.status === RecipientStatus.COMPLETED)
      const anyRecipientDeclined = !!recipients?.some(
        (r) => r.status === RecipientStatus.DECLINED && r.recipient_type === DocumentUserType.PATIENT,
      )
      const effectiveEnvelopeStatus =
        allRecipientsCompleted && doc.envelope?.status !== EnvelopeStatus.COMPLETED
          ? EnvelopeStatus.COMPLETED
          : doc.envelope?.status

      let status: DocumentStatus
      if (doc.envelope?.status === EnvelopeStatus.CREATED && !isSharedWithPatientRecipient) {
        status = DocumentStatus.DRAFT
      } else if (!isSharedWithPatientRecipient) {
        status = DocumentStatus.UPLOADED
      } else if (anyRecipientDeclined || doc.envelope?.status === EnvelopeStatus.DECLINED) {
        status = DocumentStatus.DECLINED
      } else if (hasUnviewedPatientShare && effectiveEnvelopeStatus !== EnvelopeStatus.COMPLETED) {
        status = DocumentStatus.INCOMPLETE
      } else {
        status = DocumentStatus.COMPLETED
      }
      const isOwnedByConcurrentProvider =
        doc.created_by_id in concurrentProviderNameById ||
        (concurrentEpisodeIds.has(doc.episode_id) && doc.created_by_id !== providerId)
      let addedBy: string
      let uploadedForProvider: string | undefined
      let uploadedForClient: string | undefined
      if (doc.created_by_type === DocumentUserType.PATIENT) {
        if (isDBTTeens && doc.created_by_id === patientId) {
          addedBy = teenLabel
        } else if (isDBTTeens && guardianLyraId && doc.created_by_id === guardianLyraId) {
          addedBy = caregiverLabel
        } else {
          addedBy = clientName
        }
        if (concurrentEpisodeIds.has(doc.episode_id)) {
          uploadedForProvider = providerNameByEpisodeId[doc.episode_id] ?? unknownProviderName
        } else {
          uploadedForProvider = providerFullName ?? unknownProviderName
        }
      } else if (doc.created_by_id === providerId) {
        addedBy = providerFullName ?? ''
      } else {
        addedBy = concurrentProviderNameById[doc.created_by_id] ?? unknownProviderName
      }
      if (isDBTTeens && doc.created_by_type !== DocumentUserType.PATIENT) {
        const isSharedWithTeen = patientShares.some((share) => share.recipient_id === patientId)
        const isSharedWithCaregiver = patientShares.some(
          (share) =>
            share.recipient_id === guardianLyraId ||
            share.recipient_role === EpisodeParticipantRole.SECONDARY_PATIENT ||
            share.recipient_role === EpisodeParticipantRole.PRIMARY_GUARDIAN,
        )
        if (isSharedWithTeen && isSharedWithCaregiver) {
          uploadedForClient = `${teenLabel}, ${caregiverLabel}`
        } else if (isSharedWithCaregiver) {
          uploadedForClient = caregiverLabel
        } else if (isSharedWithTeen || (!doc.shares && doc.is_shared_with_patient)) {
          uploadedForClient = teenLabel
        }
      }
      return {
        id: doc.document_id,
        fileName: doc.file_name,
        status,
        type: API_TO_UI_DOCUMENT_TYPE[doc.document_type] ?? DocumentType.OTHER,
        updatedOn: doc.update_date,
        addedBy,
        uploadedForProvider,
        uploadedForClient,
        createDate: doc.create_date,
        viewedDate: doc.patient_first_viewed_date,
        createdByType: doc.created_by_type,
        creatorId: doc.created_by_id,
        envelopeStatus: effectiveEnvelopeStatus,
        isOwnedByConcurrentProvider,
        signedCount: recipients ? recipients.filter((r) => r.status === RecipientStatus.COMPLETED).length : undefined,
        totalSigners: recipients?.length,
        clientSignedDate: recipients?.find(
          (r) => r.recipient_type === DocumentUserType.PATIENT && r.status === RecipientStatus.COMPLETED,
        )?.signed_date,
        providerSignedDate: recipients?.find(
          (r) => r.recipient_type === DocumentUserType.PROVIDER && r.status === RecipientStatus.COMPLETED,
        )?.signed_date,
      }
    })
  }, [
    documentsData,
    providerId,
    providerFullName,
    clientName,
    concurrentProviderNameById,
    concurrentEpisodeIds,
    providerNameByEpisodeId,
    unknownProviderName,
    isDBTTeens,
    teenLabel,
    caregiverLabel,
    patientId,
    guardianLyraId,
  ])

  const availableAddedBy = useMemo(() => {
    const addedBySet = new Set<string>()
    documents.forEach((doc) => {
      if (doc.addedBy) {
        addedBySet.add(doc.addedBy)
      }
    })
    return Array.from(addedBySet).sort()
  }, [documents])

  const filterSections = useMemo(
    (): FilterSectionConfig<DocumentFiltersState>[] => [
      {
        key: 'status',
        label: <FormattedMessage defaultMessage='Status' description='Status filter section' />,
        options: Object.values(DocumentStatus).map((status) => ({ id: status, text: status })),
      },
      {
        key: 'type',
        label: <FormattedMessage defaultMessage='Type' description='Type filter section' />,
        options: Object.values(DocumentType)
          .filter((type) => isDaProvider || type !== DocumentType.INTAKE_FORM)
          .map((type) => ({ id: type, text: type })),
      },
      {
        key: 'addedBy',
        label: <FormattedMessage defaultMessage='Added by' description='Added by filter section' />,
        options: availableAddedBy.map((addedBy) => ({ id: addedBy, text: addedBy })),
      },
    ],
    [availableAddedBy, isDaProvider],
  )

  const filteredDocuments = useMemo(() => {
    let filtered = documents

    if (searchTerm.trim()) {
      filtered = filtered.filter((doc) => doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    if (filterState.status.length > 0) {
      filtered = filtered.filter((doc) => filterState.status.includes(doc.status))
    }

    if (filterState.type.length > 0) {
      filtered = filtered.filter((doc) => filterState.type.includes(doc.type))
    }

    if (filterState.addedBy.length > 0) {
      filtered = filtered.filter((doc) => filterState.addedBy.includes(doc.addedBy))
    }

    return filtered
  }, [documents, searchTerm, filterState])

  const columns = useClientDocumentsColumns({ clientName, providerFullName })

  const renderRowActions = (row: Row<DocumentItem>, isHovered: boolean) => {
    if (!isHovered) return null
    const doc = row.original
    if (doc.isOwnedByConcurrentProvider) return null
    const isIncomplete = doc.status === DocumentStatus.INCOMPLETE
    const isDraft = doc.status === DocumentStatus.DRAFT
    const isDeclined = doc.status === DocumentStatus.DECLINED
    const showDownloadEditDelete =
      doc.status === DocumentStatus.COMPLETED ||
      (doc.status === DocumentStatus.UPLOADED && doc.createdByType === DocumentUserType.PATIENT)
    const showDownloadEditShareDelete =
      doc.status === DocumentStatus.UPLOADED && doc.createdByType === DocumentUserType.PROVIDER

    return (
      <RowActionsContainer>
        {isDraft && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Open draft in DocuSign',
              description: 'Tooltip for open draft in DocuSign button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleOpenDraftInDocuSign(doc)}
            testID={`ClientDocuments-openDraft-${doc.id}`}
          >
            <ActionIconContainer>
              <ExternalLinkIcon size={20} strokeColor={colors.iconSuccessMuted} />
            </ActionIconContainer>
          </Tooltip>
        )}
        {(showDownloadEditDelete || showDownloadEditShareDelete) && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Download',
              description: 'Tooltip for download document button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleDownload(doc)}
            testID={`ClientDocuments-download-${doc.id}`}
          >
            <ActionIconContainer>
              <DownloadFileIcon size={20} fill={colors.iconSuccessMuted} />
            </ActionIconContainer>
          </Tooltip>
        )}
        {isIncomplete && (doc.totalSigners ?? 0) >= 2 && (doc.signedCount ?? 0) > 0 && !doc.providerSignedDate && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Sign in DocuSign',
              description: 'Tooltip for sign in DocuSign button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleSignInDocuSign(doc)}
            testID={`ClientDocuments-signInDocuSign-${doc.id}`}
          >
            <ActionIconContainer>
              <SignatureIcon height={20} width={20} fillColor={colors.iconSuccessMuted} />
            </ActionIconContainer>
          </Tooltip>
        )}
        {(isDraft || showDownloadEditDelete || showDownloadEditShareDelete) && doc.creatorId === providerId && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Edit details',
              description: 'Tooltip for edit document button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleEditClick(doc)}
            testID={`ClientDocuments-edit-${doc.id}`}
          >
            <ActionIconContainer>
              <EditIcon size={20} fillColor={colors.iconSuccessMuted} isFilled={false} />
            </ActionIconContainer>
          </Tooltip>
        )}
        {showDownloadEditShareDelete && doc.creatorId === providerId && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Share with client',
              description: 'Tooltip for share document button',
            })}
            hoverEnabled
            placement='top'
            onPress={() => handleShareClick(doc)}
            testID={`ClientDocuments-share-${doc.id}`}
          >
            <ActionIconContainer>
              <ShareIcon size={20} strokeColor={colors.iconSuccessMuted} />
            </ActionIconContainer>
          </Tooltip>
        )}
        {(isDraft || isIncomplete || isDeclined || showDownloadEditDelete || showDownloadEditShareDelete) &&
          doc.creatorId === providerId && (
            <Tooltip
              content={intl.formatMessage({
                defaultMessage: 'Delete',
                description: 'Tooltip for delete document button',
              })}
              hoverEnabled
              placement='top'
              onPress={() => handleDeleteClick(doc)}
              testID={`ClientDocuments-delete-${doc.id}`}
            >
              <ActionIconContainer>
                <TrashIcon size={20} fillColor={colors.iconSuccessMuted} />
              </ActionIconContainer>
            </Tooltip>
          )}
      </RowActionsContainer>
    )
  }
  return (
    <View testID={tID('ClientDocuments-tab')}>
      <Container>
        <FilterRowWrapper>
          <FilterRow>
            <InputField
              testID={tID('ClientDocuments-search-input')}
              accessibilityLabel={intl.formatMessage({
                defaultMessage: 'Search documents by file name',
                description: 'Accessibility label for document search input',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Search file name',
                description: 'Search documents by file name',
              })}
              baseInputStyle={{ width: 300, marginBottom: 0 }}
              inputContainerStyle={{ borderRadius: 8, borderColor: colors.borderDefault }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.nativeEvent.text)}
              leftIcon={
                <SearchIconWrapper>
                  <SearchIcon fillColor={colors.iconDefault} />
                </SearchIconWrapper>
              }
              inputExtras={
                searchTerm ? (
                  <UnstyledIconButton
                    testID={tID('ClientDocuments-clear-button')}
                    onPress={() => setSearchTerm('')}
                    accessibilityLabel={intl.formatMessage({
                      defaultMessage: 'Clear search',
                      description: 'Clear search button',
                    })}
                  >
                    <XIcon fillColor={colors.iconDefault} />
                  </UnstyledIconButton>
                ) : undefined
              }
            />
            <DocumentFilters
              filterState={filterState}
              initialFilterState={INITIAL_DOCUMENT_FILTER_STATE}
              onFilterStateChange={setFilterState}
              sections={filterSections}
            />
          </FilterRow>
          <PrimaryButton
            text={<FormattedMessage defaultMessage='Upload' description='Upload button text' />}
            onPress={handleUpload}
            testID={tID('ClientDocuments-upload-button')}
          />
        </FilterRowWrapper>
        {isLoadingDocuments ? (
          <View style={{ padding: 48, alignItems: 'center', justifyContent: 'center' }}>
            <ActivityIndicator size='large' color={colors.iconActive} />
          </View>
        ) : (
          <DataTable
            columns={columns}
            data={filteredDocuments}
            pagination={{ pageSize: 25 }}
            rowHeight={72}
            cellAlignment='left'
            rowBorder
            striped={false}
            withRowCount
            sortingState={sortingState}
            onSortingChange={setSortingState}
            hoveredRowId={hoveredRowId}
            onRowHover={setHoveredRowId}
            onRowClick={handleRowClick}
            rowActions={renderRowActions}
            emptyTableComponent={
              <EmptyTableText>
                {intl.formatMessage({
                  defaultMessage: 'No documents found',
                  description: 'Message shown when no documents are available',
                })}
              </EmptyTableText>
            }
          />
        )}
      </Container>
      <ConfirmationModal
        visible={documentToDelete !== null}
        onRequestClose={handleDeleteCancel}
        closeOnScrim
        modalTitle={
          <FormattedMessage
            defaultMessage='Delete {fileName}'
            description='Delete document modal title'
            values={{ fileName: documentToDelete?.fileName }}
          />
        }
        modalContents={
          <FormattedMessage
            defaultMessage='This action cannot be undone. This document will be deleted from the client record. If this document was shared with {clientName}, it will no longer be visible.'
            description='Delete document modal body text'
            values={{ clientName }}
          />
        }
        cancelButtonText={
          <FormattedMessage defaultMessage='Cancel' description='Cancel button text for delete document modal' />
        }
        confirmationButtonText={
          <FormattedMessage defaultMessage='Delete' description='Delete button text for delete document modal' />
        }
        onConfirmationButtonPress={handleDeleteConfirm}
        confirmButtonLoading={isDeleting}
        confirmButtonDisabled={isDeleting}
        testID={tID('ClientDocuments-deleteModal')}
      />
      <Modal
        visible={documentToShare !== null}
        onRequestClose={handleShareCancel}
        disableBottomSheet
        showCloseIcon
        closeOnScrim
        modalContents={
          <ShareModalContent testID={tID('ClientDocuments-shareModal')}>
            <Title
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Share {fileName} with {clientName}'
                  description='Share document modal title'
                  values={{ fileName: documentToShare?.fileName, clientName }}
                />
              }
            />
            <BodyText
              text={intl.formatMessage({
                defaultMessage:
                  'This document will be added to their client portal and they will be notified immediately.',
                description: 'Share document modal body text',
              })}
            />
            <InputField
              testID={tID('ClientDocuments-uploadShareMessage')}
              label={intl.formatMessage({
                defaultMessage: 'Message (optional)',
                description: 'Optional message to send with the shared document',
              })}
              value={uploadShareMessage}
              onChange={(e) => setUploadShareMessage(e.nativeEvent.text)}
              multiline
              numberOfLines={3}
            />
            <EditModalButtonRow>
              <SecondaryButton
                text={
                  <FormattedMessage defaultMessage='Cancel' description='Cancel button text for share document modal' />
                }
                onPress={handleShareCancel}
                testID={tID('ClientDocuments-shareModal-cancel')}
                disabled={isSharing}
              />
              <PrimaryButton
                text={
                  <FormattedMessage defaultMessage='Share' description='Share button text for share document modal' />
                }
                onPress={handleShareConfirm}
                testID={tID('ClientDocuments-shareModal-share')}
                loading={isSharing}
                disabled={isSharing}
              />
            </EditModalButtonRow>
          </ShareModalContent>
        }
      />
      <ConfirmationModal
        visible={libraryConfirmVisible}
        modalTitle={
          !!selectedTemplate?.signing_flow || libraryShareDocument ? (
            <FormattedMessage
              defaultMessage='Share with {clientName}?'
              description='Library upload confirmation modal title when sharing'
              values={{ clientName }}
            />
          ) : (
            <FormattedMessage defaultMessage='Upload' description='Library upload confirmation modal title' />
          )
        }
        modalContents={
          !!selectedTemplate?.signing_flow || libraryShareDocument ? (
            <FormattedMessage
              defaultMessage="You're uploading {fileName}. This document will be added to their client portal and they'll be notified immediately."
              description='Library upload confirmation modal body when sharing'
              values={{ fileName: selectedTemplate?.file_name }}
            />
          ) : (
            <FormattedMessage
              defaultMessage='You are uploading {fileName} to {clientName} documents.'
              description='Library upload confirmation modal body'
              values={{ fileName: selectedTemplate?.file_name, clientName }}
            />
          )
        }
        cancelButtonText={
          <FormattedMessage defaultMessage='Go back' description='Go back button for library upload confirmation' />
        }
        confirmationButtonText={
          !!selectedTemplate?.signing_flow || libraryShareDocument ? (
            <FormattedMessage
              defaultMessage='Confirm and share'
              description='Confirm and share button for library upload confirmation when sharing'
            />
          ) : (
            <FormattedMessage defaultMessage='Upload' description='Upload button for library upload confirmation' />
          )
        }
        onConfirmationButtonPress={handleFromLibraryConfirm}
        onCancelPressed={() => setLibraryConfirmVisible(false)}
        onRequestClose={() => setLibraryConfirmVisible(false)}
        confirmButtonLoading={isCreatingFromTemplate}
        confirmButtonDisabled={isCreatingFromTemplate}
        testID={tID('ClientDocuments-libraryConfirmModal')}
      />
      <Modal
        visible={documentToEdit !== null}
        onRequestClose={handleEditCancel}
        disableBottomSheet
        showCloseIcon
        closeOnScrim
        modalContents={
          <EditModalContent testID={tID('ClientDocuments-editModal')}>
            <EditModalHeader>
              <Title
                size={TitleSize.MEDIUM}
                text={
                  <FormattedMessage defaultMessage='Edit document details' description='Edit document modal title' />
                }
              />
            </EditModalHeader>
            <InputField
              label={intl.formatMessage({
                defaultMessage: 'File name',
                description: 'Label for file name input field',
              })}
              value={editFileName}
              onChange={(e) => setEditFileName(e.nativeEvent.text)}
              name='editFileName'
              testID={tID('ClientDocuments-editModal-fileName')}
              maxCharLength={DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH}
            />
            <SelectField
              label={intl.formatMessage({
                defaultMessage: 'Type',
                description: 'Label for document type select field',
              })}
              value={editDocumentType}
              onChange={(value) => setEditDocumentType(value as DocumentType)}
              options={documentTypeOptions}
              name='editDocumentType'
            />
            <EditModalButtonRow>
              <SecondaryButton
                text={
                  <FormattedMessage defaultMessage='Cancel' description='Cancel button text for edit document modal' />
                }
                onPress={handleEditCancel}
                testID={tID('ClientDocuments-editModal-cancel')}
                disabled={isEditing}
              />
              <PrimaryButton
                text={
                  <FormattedMessage defaultMessage='Update' description='Update button text for edit document modal' />
                }
                onPress={handleEditConfirm}
                testID={tID('ClientDocuments-editModal-update')}
                loading={isEditing}
                disabled={isEditing}
              />
            </EditModalButtonRow>
          </EditModalContent>
        }
      />
      <FullScreenOverlay isOpen={isUploadModalOpen} isFocused={false} renderInPortal>
        <UploadModalContainer testID={tID('ClientDocuments-uploadModal')}>
          <UploadModalHeader>
            <Title
              size={TitleSize.MEDIUM}
              text={<FormattedMessage defaultMessage='Upload' description='Upload modal title' />}
            />
            <UploadModalHeaderButtons>
              <TertiaryButton
                text={<FormattedMessage defaultMessage='Exit' description='Exit button text for upload modal' />}
                onPress={handleUploadModalClose}
                testID={tID('ClientDocuments-uploadModal-exit')}
                customTextColor={colors.linkDefault}
                disabled={isUploading || isSharing || isCreatingFromTemplate}
              />
              <PrimaryButton
                text={
                  activeUploadTab !== 'fromLibrary' && uploadSignatureRequired && uploadSignatureRequired !== 'none' ? (
                    <FormattedMessage
                      defaultMessage='Next'
                      description='Next button text for upload modal when signature is required'
                    />
                  ) : (
                    <FormattedMessage defaultMessage='Upload' description='Upload button text for upload modal' />
                  )
                }
                onPress={activeUploadTab === 'fromLibrary' ? handleFromLibraryInitiate : handleUploadConfirm}
                testID={tID('ClientDocuments-uploadModal-upload')}
                loading={activeUploadTab === 'fromLibrary' ? isCreatingFromTemplate : isUploading || isSharing}
                disabled={
                  activeUploadTab === 'fromLibrary'
                    ? !selectedTemplateId || !clientDetailsSelectedEpisodeId
                    : isUploading ||
                      isSharing ||
                      !uploadFile ||
                      !uploadFileName ||
                      !uploadDocumentType ||
                      !clientDetailsSelectedEpisodeId
                }
              />
            </UploadModalHeaderButtons>
          </UploadModalHeader>
          <UploadModalContent>
            <HorizontalTabsContainer
              testID={tID('ClientDocuments-uploadModal-tabs')}
              onTabChange={(tabId) => {
                setActiveUploadTab(tabId)
                setLibraryErrorMessage(null)
              }}
              tabs={[
                {
                  id: 'fromComputer',
                  label: intl.formatMessage({
                    defaultMessage: 'From your computer',
                    description: 'Tab label for uploading from computer',
                  }),
                  content: (
                    <UploadTabContent testID={tID('ClientDocuments-uploadModal-fromComputer')}>
                      <UploadTabSplitContainer>
                        <UploadTabLeftPane>
                          <FileUpload
                            name='uploadFile'
                            label={intl.formatMessage({
                              defaultMessage: 'Upload file',
                              description: 'File upload label',
                            })}
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Click to upload or drag file here',
                              description: 'File upload placeholder text',
                            })}
                            subText={intl.formatMessage({
                              defaultMessage: 'Supported formats: JPEG, PNG, PDF, and DOCX. Max file size: 10 MB',
                              description: 'File upload supported formats and size info',
                            })}
                            onFileSelect={(file) => {
                              setUploadFile(file)
                              if (!file) {
                                setUploadFileName('')
                              } else if (!uploadFileName) {
                                setUploadFileName(file.name)
                              }
                            }}
                            selectedFile={uploadFile}
                            baseInputStyle={{ marginBottom: 20 }}
                            showPreview={false}
                            maxFileSize={DOCUMENT_UPLOAD_MAX_FILE_SIZE}
                            acceptedFileTypes={[
                              'image/jpeg',
                              'image/png',
                              'application/pdf',
                              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            ]}
                          />
                          <InputField
                            name='fileName'
                            label={intl.formatMessage({
                              defaultMessage: 'File name*',
                              description: 'File name input label',
                            })}
                            value={uploadFileName}
                            onChange={(e) => {
                              setUploadFileName(e.nativeEvent.text)
                              setUploadErrorMessage(null)
                            }}
                            error={uploadErrorMessage ?? undefined}
                            toolTipContent={intl.formatMessage({
                              defaultMessage: 'File name will be visible to clients if document is shared',
                              description: 'File name tooltip',
                            })}
                            toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                            toolTipHoverEnabled
                            baseInputStyle={{ marginBottom: 20 }}
                            maxCharLength={DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH}
                          />
                          <SelectField
                            name='documentType'
                            label={intl.formatMessage({
                              defaultMessage: 'Type*',
                              description: 'Document type dropdown label',
                            })}
                            value={uploadDocumentType}
                            onChange={(value) => setUploadDocumentType(value)}
                            options={documentTypeOptions}
                            baseInputStyle={{ marginBottom: 20 }}
                            customWebStyle={!uploadDocumentType ? { color: colors.textInactive } : undefined}
                          />
                          <SelectField
                            name='signatureRequired'
                            label={intl.formatMessage({
                              defaultMessage: 'Signature required',
                              description: 'Signature required dropdown label',
                            })}
                            value={uploadSignatureRequired}
                            onChange={(value) => {
                              setUploadSignatureRequired(value)
                              if (value && value !== 'none') setUploadShareDocument(false)
                            }}
                            options={signatureRequiredOptions}
                            toolTipContent={intl.formatMessage({
                              defaultMessage: 'Signature collected by DocuSign. Set up signature location in next step',
                              description: 'Signature required tooltip',
                            })}
                            toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                            toolTipHoverEnabled
                            baseInputStyle={{ marginBottom: 20 }}
                            customWebStyle={!uploadSignatureRequired ? { color: colors.textInactive } : undefined}
                          />
                          {(!uploadSignatureRequired || uploadSignatureRequired === 'none') && (
                            <>
                              {isDBTTeens ? (
                                <>
                                  <Checkbox
                                    name='shareWithTeen'
                                    label={intl.formatMessage({
                                      defaultMessage: 'Share document',
                                      description: 'Share document checkbox label',
                                    })}
                                    title={intl.formatMessage(
                                      {
                                        defaultMessage: '{teenFirstName} (teen)',
                                        description: 'Share document with teen checkbox text for DBT Teens program',
                                      },
                                      { teenFirstName },
                                    )}
                                    checked={uploadShareDocument}
                                    onPress={() => setUploadShareDocument(!uploadShareDocument)}
                                    toolTipContent={intl.formatMessage({
                                      defaultMessage: 'Document will be immediately sent to client',
                                      description: 'Share document tooltip',
                                    })}
                                    toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                                    toolTipHoverEnabled
                                    baseInputStyle={{ marginBottom: 8 }}
                                  />
                                  {guardianLyraId && (
                                    <Checkbox
                                      name='shareWithCaregiver'
                                      title={intl.formatMessage(
                                        {
                                          defaultMessage: '{caregiverName} (caregiver)',
                                          description:
                                            'Share document with caregiver checkbox text for DBT Teens program',
                                        },
                                        { caregiverName: caregiverDisplayName },
                                      )}
                                      checked={uploadShareWithCaregiver}
                                      onPress={() => setUploadShareWithCaregiver(!uploadShareWithCaregiver)}
                                      toolTipContent={intl.formatMessage({
                                        defaultMessage: 'Document will be immediately sent to caregiver',
                                        description: 'Share document with caregiver tooltip',
                                      })}
                                      toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                                      toolTipHoverEnabled
                                      baseInputStyle={{ marginBottom: 20 }}
                                    />
                                  )}
                                </>
                              ) : (
                                <Checkbox
                                  name='shareDocument'
                                  label={intl.formatMessage({
                                    defaultMessage: 'Share document',
                                    description: 'Share document checkbox label',
                                  })}
                                  title={intl.formatMessage(
                                    {
                                      defaultMessage: 'Share with {clientName}',
                                      description: 'Share document checkbox text',
                                    },
                                    { clientName },
                                  )}
                                  checked={uploadShareDocument}
                                  onPress={() => setUploadShareDocument(!uploadShareDocument)}
                                  toolTipContent={intl.formatMessage({
                                    defaultMessage: 'Document will be immediately sent to client',
                                    description: 'Share document tooltip',
                                  })}
                                  toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                                  toolTipHoverEnabled
                                  baseInputStyle={{ marginBottom: 20 }}
                                />
                              )}
                              {(uploadShareDocument || uploadShareWithCaregiver) && (
                                <InputField
                                  testID={tID('ClientDocuments-uploadShareMessage')}
                                  label={intl.formatMessage({
                                    defaultMessage: 'Message (optional)',
                                    description: 'Optional message to send with the shared document',
                                  })}
                                  value={uploadShareMessage}
                                  onChange={(e) => setUploadShareMessage(e.nativeEvent.text)}
                                  baseInputStyle={{ marginBottom: 20 }}
                                  multiline
                                  numberOfLines={3}
                                />
                              )}
                            </>
                          )}
                        </UploadTabLeftPane>
                        <UploadTabRightPane>
                          <FilePreviewContainer>
                            {uploadFile && filePreviewUrl ? (
                              isImageFile(uploadFile) ? (
                                <Image
                                  source={{ uri: filePreviewUrl }}
                                  alt={intl.formatMessage({
                                    defaultMessage: 'File preview',
                                    description: 'Alt text for file preview image',
                                  })}
                                  contentFit='contain'
                                  style={{ width: '100%', height: '100%' }}
                                  accessibilityIgnoresInvertColors
                                  testID={tID('upload-file-preview-image')}
                                />
                              ) : isPdfFile(uploadFile) ? (
                                <iframe
                                  src={filePreviewUrl}
                                  title={intl.formatMessage({
                                    defaultMessage: 'PDF preview',
                                    description: 'Title for PDF preview iframe',
                                  })}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                  }}
                                  data-testid={tID('upload-file-preview-pdf')}
                                />
                              ) : (
                                <FilePreviewEmptyState>
                                  <BodyText
                                    text={uploadFile.name}
                                    size={BodyTextSize.DEFAULT}
                                    color={colors.textSecondary}
                                    textAlign='center'
                                  />
                                </FilePreviewEmptyState>
                              )
                            ) : (
                              <FilePreviewEmptyState>
                                <BodyText
                                  text={intl.formatMessage({
                                    defaultMessage: 'Document preview here',
                                    description: 'Placeholder text when no file is selected',
                                  })}
                                  size={BodyTextSize.DEFAULT}
                                  color={colors.textSecondary}
                                  textAlign='center'
                                />
                              </FilePreviewEmptyState>
                            )}
                          </FilePreviewContainer>
                        </UploadTabRightPane>
                      </UploadTabSplitContainer>
                    </UploadTabContent>
                  ),
                },
                {
                  id: 'fromLibrary',
                  label: intl.formatMessage({
                    defaultMessage: 'From Document Library',
                    description: 'Tab label for uploading from document library',
                  }),
                  content: (
                    <UploadTabContent testID={tID('ClientDocuments-uploadModal-fromLibrary')}>
                      <LibraryContainer>
                        <LibraryInner>
                          {libraryErrorMessage && (
                            <InlineWarningBanner
                              text={libraryErrorMessage}
                              testId='ClientDocuments-uploadModal-libraryErrorBanner'
                              marginBottom={spacing['16px']}
                            />
                          )}
                          <Title
                            size={TitleSize.MEDIUM}
                            text={
                              <FormattedMessage
                                defaultMessage='Select a document from your library'
                                description='From library tab header'
                              />
                            }
                          />
                          <LibraryContentBorder>
                            <InputField
                              name='librarySearch'
                              placeholder={intl.formatMessage({
                                defaultMessage: 'Search file name',
                                description: 'Search library documents placeholder',
                              })}
                              value={librarySearchTerm}
                              onChange={(e) => setLibrarySearchTerm(e.nativeEvent.text)}
                              baseInputStyle={{ marginTop: 16, marginBottom: 0 }}
                              inputContainerStyle={{ borderRadius: 8, borderColor: colors.borderDefault }}
                              leftIcon={
                                <SearchIconWrapper>
                                  <SearchIcon fillColor={colors.iconDefault} />
                                </SearchIconWrapper>
                              }
                            />
                            {isLoadingTemplates ? (
                              <ActivityIndicator color={colors.iconActive} />
                            ) : filteredLibraryDocuments.length === 0 ? (
                              <LibraryEmptyState>
                                <BodyText
                                  color={colors.textSecondary}
                                  text={
                                    librarySearchTerm
                                      ? intl.formatMessage({
                                          defaultMessage: 'No documents match your search',
                                          description: 'Empty state when library search has no results',
                                        })
                                      : intl.formatMessage({
                                          defaultMessage: 'No documents in your library',
                                          description: 'Empty state when document library has no templates',
                                        })
                                  }
                                />
                              </LibraryEmptyState>
                            ) : (
                              <LibraryList>
                                {filteredLibraryDocuments.map((template, index) => (
                                  <LibraryItem
                                    key={template.document_template_id}
                                    isLast={index === filteredLibraryDocuments.length - 1}
                                  >
                                    <RadioButton
                                      title={
                                        template.signing_flow ? (
                                          <LibraryItemTitleRow>
                                            {template.file_name}
                                            <LibraryItemSignatureIconWrapper>
                                              <Tooltip
                                                content={intl.formatMessage({
                                                  defaultMessage: 'Signature required',
                                                  description: 'Tooltip for signature required icon',
                                                })}
                                                hoverEnabled
                                                placement='top'
                                              >
                                                <SignatureIcon width={16} height={16} fillColor={colors.iconDefault} />
                                              </Tooltip>
                                            </LibraryItemSignatureIconWrapper>
                                          </LibraryItemTitleRow>
                                        ) : (
                                          template.file_name
                                        )
                                      }
                                      value={template.document_template_id}
                                      selected={selectedTemplateId === template.document_template_id}
                                      onChange={(value) => {
                                        setSelectedTemplateId(value as string)
                                        setLibraryErrorMessage(null)
                                      }}
                                      buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
                                      parentContainerWidth={0}
                                      gridButtonsGap={0}
                                      buttonStyle={{ marginBottom: 0 }}
                                    />
                                  </LibraryItem>
                                ))}
                              </LibraryList>
                            )}
                          </LibraryContentBorder>
                          <LibraryShareSection>
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginBottom: spacing['8px'],
                                gap: spacing['4px'],
                              }}
                            >
                              <BodyText
                                text={intl.formatMessage({
                                  defaultMessage: 'Share document',
                                  description: 'Share document section label',
                                })}
                              />
                              <Tooltip
                                content={intl.formatMessage({
                                  defaultMessage: 'Document will be immediately sent to client',
                                  description: 'Share document tooltip for library tab',
                                })}
                                triggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                                hoverEnabled
                              />
                            </View>
                            {selectedTemplate?.signing_flow && (
                              <InlineInfoBanner
                                text={intl.formatMessage({
                                  defaultMessage:
                                    'Documents requiring a signature are sent immediately to clients after uploading.',
                                  description: 'Info banner when selected template requires a signature',
                                })}
                                backgroundColor={colors.backgroundSection}
                                textColor={colors.textSecondary}
                                marginBottom={spacing['8px']}
                              />
                            )}
                            <Checkbox
                              name='libraryShareDocument'
                              title={intl.formatMessage(
                                {
                                  defaultMessage: 'Share with {clientName}',
                                  description: 'Share with client checkbox label',
                                },
                                { clientName },
                              )}
                              checked={!!selectedTemplate?.signing_flow || libraryShareDocument}
                              readOnly={!!selectedTemplate?.signing_flow}
                              onPress={() => setLibraryShareDocument(!libraryShareDocument)}
                              baseInputStyle={{ marginBottom: 12 }}
                            />
                            {!selectedTemplate?.signing_flow && libraryShareDocument && (
                              <InputField
                                testID={tID('ClientDocuments-libraryShareMessage')}
                                label={intl.formatMessage({
                                  defaultMessage: 'Message (optional)',
                                  description: 'Optional message to send with the shared document',
                                })}
                                value={libraryShareMessage}
                                onChange={(e) => setLibraryShareMessage(e.nativeEvent.text)}
                                baseInputStyle={{ marginBottom: 20 }}
                                multiline
                                numberOfLines={3}
                              />
                            )}
                          </LibraryShareSection>
                        </LibraryInner>
                      </LibraryContainer>
                    </UploadTabContent>
                  ),
                },
              ]}
            />
          </UploadModalContent>
        </UploadModalContainer>
      </FullScreenOverlay>
      <FullScreenOverlay isOpen={isDocuSignModalOpen} isFocused={false} renderInPortal>
        <DocuSignEmbeddedView
          signingUrl={docuSignUrl}
          title={
            docuSignMode === 'sign'
              ? intl.formatMessage({
                  defaultMessage: 'Sign document',
                  description: 'DocuSign embedded view header when provider is signing a document',
                })
              : intl.formatMessage({
                  defaultMessage: 'Set up signature locations',
                  description: 'DocuSign embedded view header when setting up a document for signing',
                })
          }
          onComplete={handleDocuSignComplete}
          onCancel={handleDocuSignCancel}
          onError={handleDocuSignError}
        />
      </FullScreenOverlay>
    </View>
  )
}

export default ClientDocuments
